
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import Multiselect from "vue-multiselect";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { ShareWithPerson, EventDetails } from "@/store/site-monitor/types";
import { SessionResource } from "@/store/sessions/types";
import api from '@/services/api.service';
import vselect3 from "vselect3";
import ShareAsset from '@/types/sv-data/ShareAsset';
import EventSharingAsset from './EventSharingAsset.vue';
import MobileEventSharingStatus from '@/types/sv-data/MobileEventSharingStatus';
import { truncateString } from "@/filters";

const SessionStore = namespace("sessions");
const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		multiselect: Multiselect,
		VuePerfectScrollbar: VuePerfectScrollbar,
		"v-select3" : vselect3,
		"event-share-asset" : EventSharingAsset
	},
	filters: { truncateString }
})
export default class EventSharing extends Vue {
	@Prop(Number) eventId?: number;

	$refs!: {
		sharingModal: any;
		assetSelect: any;
	};

	public settings: any = {};
	public selected: any[] = [];
	public isLoading: boolean = false;

	public adhocName: string = "";
	public adhocEmail: string = "";

	private mobileAssetIDs: number[] = [];

	@SessionStore.Getter getSession: any;
	@SessionStore.Action updateSession: any;

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter("getIsFieldOpsLicensed") private fieldOpsLicenced: boolean;
	@SiteMonitor.Action loadExternalUsersAllowed: any;
	@SiteMonitor.Action removeShare: any;
	@SiteMonitor.Action updateSharing: any;
	@SiteMonitor.Action eventShareSearch: any;
	@SiteMonitor.Action eventPassControl: any;
	@SiteMonitor.Action loadIntegrationTemplate: any;
	@SiteMonitor.Action shareEventWithUser: any;
	@SiteMonitor.Action shareEventWithGroup: any;
	@SiteMonitor.Action addAdhocEventShare: any;

	@SiteMonitor.Getter("getExternalUsersAllowed") externalEventShare!: boolean;
	@SiteMonitor.Getter("getEventShareShown") eventShareShown!: boolean;
	@SiteMonitor.Getter("getEventShare") sharedWith!: any[];
	@SiteMonitor.Getter shareWithPersons: ShareWithPerson[];
	@SiteMonitor.Getter("getAdhocShareDetails") adhocShareDetails: any;
	@SiteMonitor.Getter getEventDetails: EventDetails;
	@SiteMonitor.State("MobileEventShares") mobileEventShares: MobileEventSharingStatus[];

	@SiteMonitor.Mutation setEventShareShown: any;
	@SiteMonitor.Mutation setAdHocShareDetails: any;
	@SiteMonitor.Mutation setActivity: any;

	private assets: ShareAsset[] = [];

	private get canMobileEventShare() : boolean {
		return this.getFeature(["Mobile", "FieldOps", "EventShare"]) && this.fieldOpsLicenced;
	}

	private async loadAssets() : Promise<void> {
		const assets = await api.getShareableAssets(this.eventId);
		//An asset can only be actively attached to one event at a time, filter out any assets that have an active event
		this.assets = assets.filter(ast => ast.isUser || (!ast.isUser && !ast.hasActiveShare));
	}

	private get mobileEventShareAssets() {
		if(this.sharedWith && this.sharedWith.length > 0 && this.mobileEventShares){
			return this.mobileEventShares.filter(x => !this.sharedWith.map(y => y.userID).includes(x.userID));
		}
		return this.mobileEventShares;
	}

	private getMobileEventShareForUser(userID: number){
		if(!this.mobileEventShares || this.mobileEventShares.length == 0)
			return null;

		const mobileShare = this.mobileEventShares.find(x => x.userID === userID);
		return mobileShare ? mobileShare : null;
	}

	private async mobileShare(): Promise<void> {
		const mobileAssetIDsCopy = [...this.mobileAssetIDs];
		for (const assetID of mobileAssetIDsCopy) {
			await api.shareEventWithAsset(this.eventId, assetID);
		}
		this.updateSharing({ eventID: this.eventId });

		this.$refs.assetSelect.clearSelection();
		this.mobileAssetIDs = [];
	}

	private getMobileShareBackground(share: any): string {
		return `background-color:${share.iconColor ? share.iconColor : 'grey' };`
	}

	@Watch("eventShareShown")
	onEventCloseShownChanged(value: boolean) {
		if (value) {
			this.loadAssets(); //reload the assets every time open the share
			this.$refs.sharingModal.show();
		} else {
			this.$refs.sharingModal.hide();
		}
	}

	created() {
		this.setEventShareShown(false);
		this.updateSession({ resourceId: SessionResource.DeviceServiceSession }); // eventId?
		this.loadExternalUsersAllowed(this.eventId);
		this.loadAssets();
	}

	async eventShareFind(query: any) {
		this.isLoading = true;
		try {
			await this.eventShareSearch({ 
				query: query,
				eventId: this.eventId
			});
			this.isLoading = false;
		} catch (e) {
			this.isLoading = false;
		}
			this.setActivity();
	}

	async addImmixShare() {
			this.setActivity();
		let sharePromises: Promise<any>[] = [];
		this.selected.forEach(share => {
			if (share.userID != null) {
				sharePromises.push(
					this.shareEventWithUser({
						UserID: share.userID,
						EventID: this.eventId,
						PassControl: false
					})
				);
			} else if (share.routingGroupID != null) {
				sharePromises.push(
					this.shareEventWithGroup({
						RoutingGroupID: share.routingGroupID,
						EventID: this.eventId,
						PassControl: false
					})
				);
			}
		});

		this.selected = [];

		let shareResults = [];

		for (var i = 0; i < sharePromises.length; i++) {
			shareResults.push(await sharePromises[i]);
		}

		this.updateSharing({ eventID: this.eventId });
	}

	async addAdhocShare() {
		// removed
	}

	closeSharing() {
			this.setActivity();
		this.setEventShareShown(false);
	}

	async passControl(share: any) {
		await this.eventPassControl({
			eventID: this.eventId,
			userID: share.userID
		});
		this.closeSharing();
	}

	public removeEventShare(share: any) {
		this.setActivity();
		if(share.assetID)
			this.removeShare({ eventID: this.eventId, uniqueID: share.assetID, isMobile: true });
		else
			this.removeShare({ eventID: this.eventId, uniqueID: share.eventUserID, isMobile: false });

	}
}
